import { Box, Button, Group, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconRefresh, IconUsers } from '@tabler/icons';
import { useNavigate } from 'react-router-dom';
import { SearchInput, UsersTable } from '../../components';
import { useUsers } from '../../queries/use-users';
import { useTableState } from '../../hooks/use-table-state';
import { PAGE_SIZE } from '../../magic-values';
import type { AdminUserDoc } from '../../types/user';

export function UsersContainer() {
  const navigate = useNavigate();
  const { page, sortBy, sortDir, sortStatus, filterBy, setPage, setSortStatus, setFilterBy } =
    useTableState();
  const { data, isLoading, isFetching, isError, error, refetch } = useUsers(
    filterBy ? { filterBy } : undefined,
    {
      page,
      pageSize: PAGE_SIZE,
      sortBy,
      sortDir,
    },
  );
  const users = data?.items ?? [];

  const handleRowClick = (user: AdminUserDoc) => {
    navigate(`/user/${user._id}`);
  };

  return (
    <Stack spacing="xl">
      <Box>
        <Group position="apart" align="flex-start" noWrap>
          <Group spacing="md" align="center" noWrap>
            <ThemeIcon size={42} radius="md" variant="light" color="bagel">
              <IconUsers size={22} />
            </ThemeIcon>
            <Box>
              <Title order={2}>Users</Title>
              <Text size="sm" color="dimmed">
                All users in the system
                {data ? ` · ${data.total} total` : ''}
              </Text>
            </Box>
          </Group>
          <Group spacing="sm" noWrap>
            <SearchInput value={filterBy} onChange={setFilterBy} placeholder="Search users…" />
            <Button
              leftIcon={<IconRefresh size={16} />}
              variant="subtle"
              color="bagel"
              size="sm"
              onClick={() => refetch()}
              loading={isFetching}
            >
              Refresh
            </Button>
          </Group>
        </Group>
      </Box>
      {isError ? (
        <Text color="red">
          Failed to load users: {(error as Error)?.message ?? 'Unknown error'}
        </Text>
      ) : (
        <Paper withBorder sx={{ overflowX: 'auto' }}>
          <UsersTable
            users={users}
            fetching={isLoading}
            onRowClick={handleRowClick}
            page={page}
            totalRecords={data?.total ?? 0}
            onPageChange={setPage}
            sortStatus={sortStatus}
            onSortStatusChange={setSortStatus}
          />
        </Paper>
      )}
    </Stack>
  );
}
